import React from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';

import { useLivePageModeFlag } from '../useLivePageModeFlag.ts';

import { LivePagesModeChangerDropdown } from './LivePagesModeChangerDropdown.tsx';

export type LivePageMode = 'edit' | 'view';
export interface LivePagesModeChangerProps {
	contentId: string;
	isDisabled?: boolean;
	flags: FlagsStateContainer;
	analyticsSource?: string;
	elemAfter?: ({ shortcutKeys, id }: { shortcutKeys: string[]; id: string }) => React.ReactNode;
}

export const LivePagesModeChanger = ({
	contentId,
	isDisabled,
	flags,
	analyticsSource,
	elemAfter,
}: LivePagesModeChangerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ mode: livePageMode }, { setMode: setLivePageMode }] = useLivePageMode();
	const { showLivePageModeFlag } = useLivePageModeFlag(flags);

	const handleModeChange = (newMode: LivePageMode) => {
		showLivePageModeFlag(newMode);
		void setLivePageMode(newMode);

		createAnalyticsEvent?.({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'changed',
				actionSubject: 'mode',
				actionSubjectId: 'livePagesModeSwitcher',
				attributes: {
					previousMode: livePageMode,
					newMode,
				},
				objectType: 'page',
				objectId: contentId,
			},
		})?.fire();
	};

	return (
		<LivePagesModeChangerDropdown
			selectedMode={livePageMode ?? 'view'}
			onChange={handleModeChange}
			isDisabled={isDisabled}
			elemAfter={elemAfter}
		/>
	);
};
